import React, { useMemo } from 'react'
import { Form } from 'antd'
import {
  difference,
  filter,
  get,
  has,
  intersection,
  isBoolean,
  isEmpty,
  isNaN,
  isNil,
  isNull,
  join,
  keys,
  some,
  toNumber,
  toString,
} from 'lodash'
import PropTypes from 'prop-types'

import { idPropType } from '../../../common/prop-types'
import { getFormItemLabelAboveConfiguration } from '../../../common/utils'

import { INGREDIENT_SPECIAL_CASE_DEFINITION_DEFAULT_VALUES } from '../../constants'

import IngredientSpecialCaseJsonEditor from './ingredient-special-case-json-editor'

const IngredientSpecialCaseDefinitionFormItem = ({
  id,
  name,
  label,
  labelAbove,
  form,
  disabled,
}) => {
  const caseDefinitionRules = useMemo(
    () => [
      ({ getFieldValue }) => ({
        validator(_, value) {
          const type = getFieldValue('type')

          const defaultKeys = keys(
            get(INGREDIENT_SPECIAL_CASE_DEFINITION_DEFAULT_VALUES, type),
          )

          const dataKeys = keys(value)

          const extraKeys = difference(dataKeys, defaultKeys)
          const missingKeys = difference(defaultKeys, dataKeys)

          if (!isEmpty(extraKeys)) {
            return Promise.reject(
              new Error(`Extra keys are not allowed: ${join(extraKeys, ', ')}`),
            )
          }

          if (!isEmpty(missingKeys)) {
            return Promise.reject(
              new Error(`Missing keys: ${join(missingKeys, ', ')}`),
            )
          }

          const singleIngredientFields = intersection(
            ['target_ingredient', 'exclude_rule'],
            defaultKeys,
          )

          const invalidSingleIngredientFieldNames = filter(
            singleIngredientFields,
            field => {
              const singleIngredientName = get(value, field)

              return (
                !isNull(singleIngredientName) &&
                isEmpty(toString(singleIngredientName))
              )
            },
          )

          if (!isEmpty(invalidSingleIngredientFieldNames)) {
            return Promise.reject(
              new Error(
                `The following fields should be text or null: ${join(
                  invalidSingleIngredientFieldNames,
                  ', ',
                )}`,
              ),
            )
          }

          const multipleIngredientFields = intersection(
            ['default_ingredients', 'tokens', 'ingredients_to_check'],
            defaultKeys,
          )

          const invalidMultipleIngredientFieldNames = filter(
            multipleIngredientFields,
            field => {
              const multipleIngredientValues = get(value, field)

              return some(multipleIngredientValues, ingredientName =>
                isEmpty(toString(ingredientName)),
              )
            },
          )

          if (!isEmpty(invalidMultipleIngredientFieldNames)) {
            return Promise.reject(
              new Error(
                `The following fields should only contain text values: ${join(
                  invalidMultipleIngredientFieldNames,
                  ', ',
                )}`,
              ),
            )
          }

          if (
            has(value, 'exclude_pair') &&
            !isBoolean(get(value, 'exclude_pair')) &&
            get(value, 'exclude_pair') !== 'true' &&
            get(value, 'exclude_pair') !== 'false'
          ) {
            return Promise.reject(
              new Error('exclude_pair can only get "true" or "false"'),
            )
          }

          if (
            has(value, 'shift_indexes') &&
            !isEmpty(get(value, 'shift_indexes')) &&
            some(get(value, 'shift_indexes'), shiftIndex =>
              isNaN(toNumber(shiftIndex)),
            )
          ) {
            throw new Error('shift_indexes can only be a list integer number')
          }

          return Promise.resolve()
        },
      }),
    ],
    [],
  )

  return (
    // due to the json editor being a custom field, we cannot use the "dependencies" prop of the Form.Item
    // to only re-render when the type is modified. Thus we are forced to use "shouldUpdate" which updates
    // the form item whenever the form is updated.
    <Form.Item shouldUpdate>
      {() => (
        <Form.Item
          name={name}
          labelCol={getFormItemLabelAboveConfiguration(labelAbove)}
          label={label}
          rules={caseDefinitionRules}
        >
          <IngredientSpecialCaseJsonEditor
            isNew={isNil(id)}
            form={form}
            name={name}
            disabled={disabled}
          />
        </Form.Item>
      )}
    </Form.Item>
  )
}

IngredientSpecialCaseDefinitionFormItem.propTypes = {
  name: PropTypes.string.isRequired,
  labelAbove: PropTypes.bool,
  label: PropTypes.string,
  id: idPropType,
  form: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
}

IngredientSpecialCaseDefinitionFormItem.defaultProps = {
  label: 'Case definition',
  labelAbove: false,
  id: undefined,
  disabled: false,
}

export default IngredientSpecialCaseDefinitionFormItem
